import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Header, scrollToId } from "@/components/Header";
import { FeeCheckForm } from "@/components/FeeCheckForm";
import { MobileActionBar } from "@/components/MobileActionBar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useSeo, SITE_URL } from "@/hooks/useSeo";

const fade = (delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] },
});

const IMAGE_REVEAL = {
  initial: { clipPath: "inset(100% 0% 0% 0%)" },
  animate: { clipPath: "inset(0% 0% 0% 0%)" },
  transition: { duration: 1.4, delay: 0.4, ease: [0.16, 1, 0.3, 1] },
};

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1711606815631-38d32cdaec3e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const FEE_LAYERS = [
  {
    n: "01",
    title: "Adviser or advice company fees",
    body:
      "This is the fee paid for ongoing strategic advice and portfolio management. In the traditional model it is charged as an ongoing asset-based percentage — so the more you accumulate, the more you pay every year, regardless of how much work is actually done. At NUA Private Wealth we have removed asset-based pricing entirely: our advice fee is fixed, charged flexibly and agreed in writing before any work begins. Our fees are also capped at an upper limit.",
  },
  {
    n: "02",
    title: "Investment fees",
    body: [
      "The MER (Management Expense Ratio), ICR (Indirect Cost Ratio) or ‘investment fee’ is an annual fee charged directly by an investment option or managed fund on the funds the manager has under administration. The fee is deducted by the manager before your returns are published so you do not see this fee coming from your account directly. However, this fee impacts your returns. It is important to remember, a high fee is not always bad, but it can be. A low fee is not always good, but it can be. If the fee is 1% or more, you should ask why.",
      "Because these operational costs are inherent to managed investments, this fee is largely unavoidable for investors. However, maintaining awareness of your “investment fee” is essential, as even seemingly small differences, compound significantly over a working lifetime and directly reduce your net investment returns.",
    ],
  },
  {
    n: "03",
    title: "Platform fees",
    body:
      "Wrap platforms and super administration providers charge a fee—either as a flat dollar amount or a percentage of your account balance—to manage investments, conduct tax reporting, and execute portfolio performance tracking. These fees vary depending on the specific provider, platform features, and the range of available investment options.",
  },
];

export default function FeeHealthCheckPage() {
  const imgWrapRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgWrapRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSeo({
    title: "Advice Fee Health Check — NUA Private Wealth",
    description:
      "Understand the three layers of financial planning fees — adviser fees, investment fees and platform fees — and how they erode retirement savings over time. Book a complimentary advice fee health check with NUA Private Wealth.",
    path: "/advice-fee-health-check",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Advice fee health check",
        name: "Advice Fee Health Check",
        description:
          "A complimentary review of the fees you pay across adviser, investment and platform layers, benchmarked against the market.",
        url: `${SITE_URL}/advice-fee-health-check`,
        provider: { "@type": "FinancialService", name: "NUA Private Wealth" },
        areaServed: ["Canberra", "Sydney", "New South Wales", "Albury-Wodonga", "Australia"],
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          {
            "@type": "ListItem",
            position: 2,
            name: "Advice Fee Health Check",
            item: `${SITE_URL}/advice-fee-health-check`,
          },
        ],
      },
    ],
  });

  return (
    <>
      <Header />
      <main>
        <section data-testid="fee-check-hero" className="relative overflow-hidden bg-[#F9F8F4] pt-16 md:pt-[88px]">
          <div className="relative mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
            <div className="md:col-span-7 pt-10 md:pt-20">
              <motion.img
                src="/assets/nua-mark-transparent.png"
                alt=""
                aria-hidden="true"
                data-testid="fee-check-triskele"
                {...fade(0)}
                className="mb-8 h-10 md:h-12 w-auto opacity-90"
              />
              <motion.p
                data-testid="fee-check-overline"
                {...fade(0.1)}
                className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#0A2333]"
              >
                <span className="h-px w-10 bg-[#0A2333]" aria-hidden="true" />
                Advice Fee Health Check
              </motion.p>

              <h1
                data-testid="fee-check-heading"
                className="mt-8 font-serif text-4xl md:text-6xl tracking-tight leading-[1.05] text-[#111413]"
              >
                Do you know what your advice really costs?
              </h1>

              <motion.p
                data-testid="fee-check-subhead"
                {...fade(0.5)}
                className="mt-5 font-serif italic text-xl md:text-2xl text-[#0A2333]"
              >
                Most people are paying three layers of fees — and can only name one.
              </motion.p>

              <motion.p
                data-testid="fee-check-subcopy"
                {...fade(0.7)}
                className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[#4A524D]"
              >
                Fees in financial planning are rarely charged in a single, visible line. They
                are layered, bundled and deducted before you see a return — which makes them
                easy to overlook and expensive to ignore. Our complimentary fee health check
                shows you exactly what you are paying, and where, versus the market.
              </motion.p>

              <motion.div {...fade(0.85)} className="mt-10 flex flex-wrap items-center gap-5">
                <button
                  data-testid="fee-check-cta-book"
                  onClick={() => scrollToId("contact")}
                  className="inline-flex items-center gap-2 rounded-full bg-[#0A2333] px-7 py-3.5 text-xs uppercase tracking-[0.15em] text-[#F9F8F4] transition-[background-color,transform] duration-300 hover:bg-[#071824] hover:-translate-y-0.5"
                >
                  Advice fee health check
                  <ArrowUpRight size={15} />
                </button>
                <Link
                  to="/"
                  data-testid="fee-check-cta-home"
                  className="text-xs uppercase tracking-[0.15em] text-[#111413] underline decoration-[#A78558] decoration-2 underline-offset-8 hover:text-[#0A2333] transition-colors duration-300"
                >
                  Back to home
                </Link>
              </motion.div>
            </div>

            <div className="md:col-span-5 md:pt-12 pb-16 md:pb-24">
              <motion.div
                ref={imgWrapRef}
                {...IMAGE_REVEAL}
                className="relative overflow-hidden rounded-t-[10rem] h-[52vh] md:h-[70vh] border border-black/10"
                data-testid="fee-check-image-frame"
              >
                <motion.img
                  src={HERO_IMAGE}
                  alt="Calculator, charts and planning documents laid out on a desk"
                  style={{ y }}
                  className="absolute inset-0 h-[120%] w-full object-cover"
                  data-testid="fee-check-image"
                />
              </motion.div>
              <motion.p {...fade(1.15)} className="mt-4 text-[10px] uppercase tracking-[0.25em] text-[#4A524D]">
                Every fee, laid out in plain sight.
              </motion.p>
            </div>
          </div>
        </section>

        <section data-testid="fee-check-layers" className="bg-[#EFEBE2] py-16 md:py-36">
          <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <div className="md:sticky md:top-28">
                <Reveal>
                  <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#0A2333]">
                    <span className="h-px w-10 bg-[#0A2333]" aria-hidden="true" />
                    The three layers
                  </p>
                  <h2
                    data-testid="fee-check-layers-heading"
                    className="mt-6 font-serif text-3xl md:text-4xl tracking-tight text-[#111413]"
                  >
                    Where your fees actually go.
                  </h2>
                  <p className="mt-6 text-base leading-relaxed text-[#4A524D]">
                    Understanding these three layers is the first step to knowing whether you
                    are getting genuine value — or simply paying a percentage of your wealth
                    every year for the privilege of holding it.
                  </p>
                </Reveal>
              </div>
            </div>

            <div className="md:col-span-8">
              {FEE_LAYERS.map((item, i) => (
                <Reveal key={item.title} delay={i * 0.06}>
                  <div
                    data-testid={`fee-check-layer-${i + 1}`}
                    className="grid grid-cols-12 items-baseline gap-x-4 gap-y-2 border-t border-black/15 px-2 md:px-4 py-8 transition-[background-color] duration-300 hover:bg-[#F9F8F4]"
                  >
                    <span className="col-span-2 md:col-span-1 font-serif italic text-lg text-[#A78558]">
                      {item.n}
                    </span>
                    <h3 className="col-span-10 md:col-span-4 font-serif text-2xl md:text-3xl tracking-tight text-[#111413]">
                      {item.title}
                    </h3>
                    <div className="col-span-10 col-start-3 md:col-span-7 md:col-start-6 space-y-3 text-sm leading-relaxed text-[#4A524D]">
                      {(Array.isArray(item.body) ? item.body : [item.body]).map((para, pi) => (
                        <p key={`${item.n}-${pi}`}>{para}</p>
                      ))}
                    </div>
                  </div>
                </Reveal>
              ))}
              <div className="border-t border-black/15" aria-hidden="true" />
            </div>
          </div>
        </section>

        <section data-testid="fee-check-difference" className="bg-[#F9F8F4] py-16 md:py-32 border-t border-black/10">
          <div className="mx-auto max-w-4xl px-6 md:px-10 text-center">
            <Reveal>
              <p className="flex items-center justify-center gap-3 text-xs uppercase tracking-[0.25em] text-[#0A2333]">
                <span className="h-px w-10 bg-[#0A2333]" aria-hidden="true" />
                The NUA difference
                <span className="h-px w-10 bg-[#0A2333]" aria-hidden="true" />
              </p>
              <h2 className="mt-6 font-serif text-3xl md:text-5xl tracking-tight text-[#111413]">
                A fee health check costs you nothing.
              </h2>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-[#4A524D]">
                We will map every layer of fees you are currently paying, compare it against the
                market, and show you — in plain numbers — what it is costing your future. No
                obligation, no pitch, and no percentage of your assets. Just clarity.
              </p>
              <div className="mt-10 flex justify-center">
                <button
                  data-testid="fee-check-difference-cta"
                  onClick={() => scrollToId("contact")}
                  className="inline-flex items-center gap-2 rounded-full bg-[#0A2333] px-8 py-4 text-xs uppercase tracking-[0.15em] text-[#F9F8F4] transition-[background-color,transform] duration-300 hover:bg-[#071824] hover:-translate-y-0.5"
                >
                  Advice fee health check
                  <ArrowRight size={15} />
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        <FeeCheckForm />
      </main>
      <Footer />
      <MobileActionBar />
    </>
  );
}
